import {AutoSave, Card, DataStatus, Form} from 'platform/components';
import {Grid, GridItem, Show, VStack} from 'platform/foundation';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  interestSearchParams,
  PriceReportWidget,
  queryParams,
  Section,
  useInterestEditPermissions,
  usePermissions,
  useTenant,
} from '@dms/shared';

import {useQueryState, useToggle} from 'shared/web';

import {FeaturesPicker} from '../../components/FeaturesPicker';
import {RequestSpecification} from '../../components/RequestSpecification';
import {useInterestData} from '../../hooks/useInterestData';
import {BuyingVehicleForm} from './components/BuyingVehicleForm';
import {BuyingVehicleWidget} from './components/BuyingVehicleWidget';
import {Offer} from './components/Offer';
import {useMinisaleUpdate} from './hooks/useMinisaleUpdate';
import {BuyingFormType} from './types/BuyingFormType';
import {transformMinisaleToBuyingForm} from './utils/transformMinisaleToBuyingForm';

interface BuyingProps {
  interestId: string;
}

export function Buying(props: BuyingProps) {
  const [, setActiveSectionId] = useQueryState(queryParams.COMPONENT_SECTIONS_SECTION);

  const [canReadPriceReportWidget] = usePermissions({permissionKeys: ['createVehiclePriceReport']});

  const [isFeaturesOpen, toggleFeaturesOpen] = useToggle();

  const {interest, miniSale, isLoading, isError, isActionsDisabled} = useInterestData(
    props.interestId
  );
  const {tenantCurrency} = useTenant();

  const {onBuyingUpdate} = useMinisaleUpdate(props.interestId);

  const {hasChangeMinisalePriceOfferedInterestPermission} = useInterestEditPermissions({
    interestId: props.interestId,
  });

  const isInterestClosedAnyStatus =
    interest?.state === 'CLOSED' || interest?.state === 'UNSUCCESSFUL';

  return (
    <Section
      heading={i18n.t('entity.interest.labels.buyingTab')}
      actions={[
        {
          type: 'button',
          variant: 'outlined',
          title: i18n.t('general.actions.continue'),
          'data-testid': testIds.interest.detail('continue'),
          isDisabled: !miniSale || isActionsDisabled,
          onClick: () => setActiveSectionId(interestSearchParams.interestDetail.overview),
        },
      ]}
    >
      <DataStatus isLoading={isLoading} isError={isError} minHeight={200}>
        <Form<BuyingFormType>
          defaultValues={transformMinisaleToBuyingForm(miniSale, tenantCurrency)}
          isDisabled={!hasChangeMinisalePriceOfferedInterestPermission || isInterestClosedAnyStatus}
        >
          {(control, formApi) => {
            const features = formApi.watch('vehicleData.features');
            const vehicleType = formApi.watch('vehicleData.type')?.[0];
            return (
              <Grid columns={12} verticalSpacing={3}>
                <AutoSave<BuyingFormType> onSave={onBuyingUpdate} />
                <GridItem span={12}>
                  <Card variant="inlineWhite">
                    <BuyingVehicleWidget
                      data-testid={testIds.interest.detail('buying')}
                      interestId={props.interestId}
                      control={control}
                      formApi={formApi}
                    />
                  </Card>
                </GridItem>
                <GridItem span={7}>
                  <VStack spacing={3}>
                    <Card
                      variant="inlineWhite"
                      title={i18n.t('entity.vehicle.labels.vehicleSpecification')}
                    >
                      <BuyingVehicleForm
                        data-testid={testIds.interest.detail('buying')}
                        control={control}
                        formApi={formApi}
                        onSave={onBuyingUpdate}
                      />
                    </Card>

                    <Card variant="inlineWhite" title={i18n.t('entity.vehicle.labels.features')}>
                      <VStack spacing={4}>
                        {isNotNilOrEmpty(features) ? (
                          <RequestSpecification
                            data-testid={testIds.interest.detail('buying')}
                            filteredSpecifications={{features}}
                          />
                        ) : null}
                        <FeaturesPicker<BuyingFormType>
                          data-testid={testIds.interest.detail('buying')}
                          control={control}
                          name="vehicleData.features"
                          vehicleType={vehicleType}
                          isFeaturesOpen={isFeaturesOpen}
                          toggleFeaturesOpen={toggleFeaturesOpen}
                          isDisabled={isInterestClosedAnyStatus}
                          isLabelHidden
                        />
                      </VStack>
                    </Card>
                  </VStack>
                </GridItem>

                <GridItem span={5}>
                  <VStack spacing={3}>
                    <Offer
                      data-testid={testIds.interest.detail('buying')}
                      interestId={props.interestId}
                      control={control}
                      formApi={formApi}
                    />

                    <Show when={canReadPriceReportWidget}>
                      <Card>
                        <PriceReportWidget
                          resourceId={props.interestId}
                          vehicleType={miniSale?.vehicleData?.type ?? 'VEHICLETYPE_PASSENGER_CAR'}
                          context="interest"
                        />
                      </Card>
                    </Show>
                  </VStack>
                </GridItem>
              </Grid>
            );
          }}
        </Form>
      </DataStatus>
    </Section>
  );
}
